import type { FC } from 'react';
import React, { useState } from 'react';

import { Button, ClickableDiv } from '@wwe-portal/ui/components/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@wwe-portal/ui/components/tooltip';
import type { OfferCardProps } from '@wwe-portal/ui/features/offer/type';
import lockIconWebp from '@wwe-portal/ui/assets/images/flagship/lock-icon.webp';

import type { ExclusiveItemProps } from './exclusive-item';

export interface BuyButtonProps {
  isBlocked: boolean;
  isLoading: boolean;
  exclusiveItem: ExclusiveItemProps;
  onClick: (exclusiveItem: ExclusiveItemProps) => void;
  offerData: OfferCardProps;
  isMobile?: boolean;
}
const BuyButton: FC<BuyButtonProps> = ({
  isBlocked,
  isLoading,
  exclusiveItem,
  offerData,
  onClick,
  isMobile = true,
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);

  const getPurchaseTitle = () => {
    if (isLoading) return 'Processing';
    if (offerData.priceNum) return 'Buy';

    return offerData.price;
  };

  const handleClick = () => {
    if (!isBlocked) {
      return onClick(exclusiveItem);
    }
    setOpenTooltip(true);
  };

  const buyButtonToolTip = ({ children }: { children: React.ReactNode }) => {
    return (
      <div data-enabled>
        <Tooltip onOpenChange={setOpenTooltip} open={openTooltip}>
          <TooltipTrigger asChild>
            <ClickableDiv
              onClick={(e) => {
                e.stopPropagation();
                setOpenTooltip((currentOpen) => !currentOpen);
              }}
            >
              {children}
            </ClickableDiv>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>
              Please subscribe higher
              <br />
              plan to buy this item
              <TooltipArrow />
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="relative" style={{ width: isMobile ? '100%' : '' }}>
      {isMobile ? (
        <div className="flex w-full items-center justify-center">
          <Button
            className="hover:bg-silvertier active:bg-silvertier bg-silvertier h-10 w-full rounded-md text-xs font-semibold leading-[18px] text-white"
            loading={isLoading}
            onClick={handleClick}
          >
            {getPurchaseTitle()}
          </Button>
        </div>
      ) : (
        <Button
          className="3xl:w-[100px] flex-0 bg-goldtier font-poppins h-8 w-[112px] rounded-md text-xs font-semibold leading-[18px] hover:bg-none"
          loading={isLoading}
          onClick={handleClick}
        >
          {isLoading ? (
            'Processing'
          ) : (
            <div className="flex items-center justify-center gap-x-1 text-white">
              {offerData.price}
            </div>
          )}
        </Button>
      )}
      {Boolean(isBlocked) && (
        <div className="absolute right-3 top-0 flex h-full cursor-pointer items-center justify-center md:right-1">
          <div data-enabled>
            {buyButtonToolTip({
              children: (
                <img
                  alt="locked"
                  className="h-[21px] md:h-[22px]"
                  src={lockIconWebp}
                />
              ),
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyButton;
